import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button, Col, Row } from 'react-bootstrap';

import { useAppSelector } from 'hooks';
import { formatObservations } from 'utils/MeasurementsStoreFunctions';
import { SectionTitle } from 'components/UI';
import { isPatientArchived } from 'store/patient/reducer';

import Observation from './Observation';
import AddEditObservationModal from './Observation/AddEditObservationModal';

interface IObservationsProps {
  observations: ICarePlanObservationTask[] | undefined;
  getCarePlan: any;
  carePlanTemplate?: ICarePlanTemplate;
}

const Observations = ({ observations, getCarePlan, carePlanTemplate }: IObservationsProps) => {
  const intl = useIntl();
  const [showAddModal, setShowAddModal] = useState(false);
  const [_observations, setObservations] = useState(observations || []);
  const [selectedObservation, setSelectedObservation] = useState<
    ICarePlanObservationTask | undefined
  >(undefined);
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';
  const _isPatientArchived = useAppSelector(isPatientArchived);
  const handleOpenAddModal = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSelectedObservation(undefined);
  };

  const handleAddObservation = () => {
    handleCloseAddModal();
    getCarePlan();
  };

  const onSelectObservation = (observation: ICarePlanObservationTask) => {
    setSelectedObservation(observation);
    setShowAddModal(true);
  };
  const onDeleteObservation = (id: string) => {
    setObservations(_observations.filter((elem) => elem.id !== id));
  };

  useEffect(() => {
    const formattedObservations = formatObservations(observations || []);
    setObservations(formattedObservations || []);
  }, [observations]);

  return (
    <div className="mb-5" data-test="careplan-observations-section">
      <div className="d-flex justify-content-between align-items-center">
        <SectionTitle
          dataTestId="careplan-observations-title"
          label={intl.formatMessage({ id: 'observationActivitiesKey' })}
          pill="APP"
        />
        {!isTemplateGlobal && (
          <Button
            data-test="careplan-add-observation-btn"
            variant="link"
            size="sm"
            onClick={handleOpenAddModal}
            disabled={_isPatientArchived}
          >
            + {intl.formatMessage({ id: 'observationActivitiesKey' })}
          </Button>
        )}
      </div>
      <Row>
        {_observations && _observations.length > 0 ? (
          _observations.map((observation, index) => (
            <Observation
              dataTestId={`careplan-observation-${index}`}
              key={observation.id}
              observation={observation}
              onDeleteObservation={onDeleteObservation}
              onShowEditModal={() => onSelectObservation(observation)}
              carePlanTemplate={carePlanTemplate}
              disabled={_isPatientArchived}
            />
          ))
        ) : (
          <Col>
            <div
              data-test="careplan-observations-empty"
              className="bg-light text-center p-3 font-weight-bolder"
            >
              {intl.formatMessage({ id: 'noScheduledObservationActivities' })}
            </div>
          </Col>
        )}
      </Row>

      {showAddModal && (
        <AddEditObservationModal
          show={showAddModal}
          handleClose={handleCloseAddModal}
          handleAdd={handleAddObservation}
          carePlanTemplate={carePlanTemplate}
          observation={selectedObservation}
        />
      )}
    </div>
  );
};

export default Observations;
